import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { XIcon } from './Icons';

const TextInput = ({
  value,
  onChange,
  name,
  placeholder = "Enter text",
  required = false,
  readOnly = false,
  disabled = false,
  className,
  type = "text",
  maxLength,
  minLength,
  showCharacterCount = false,
  showClearButton = true,
  autoComplete,
  autoFocus = false,
  pattern,
  inputMode,
  size = "default",
  variant = "default",
  error,
  helperText,
  prefix,
  suffix,
  ...props
}) => {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Handle input change
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: newValue,
          type: 'text'
        }
      });
    }
  };

  // Handle clear button
  const handleClear = (event) => {
    event.stopPropagation();
    setInputValue('');
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
          type: 'text'
        }
      });
    }
    
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle focus and blur
  const handleFocus = (event) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  const handleBlur = (event) => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    default: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  // Variant styles
  const variantClasses = {
    default: `${theme.colors.surface} ${theme.text.primary} ${theme.border.light}`,
    filled: `bg-gray-100 dark:bg-gray-800 ${theme.text.primary} border-transparent`,
    outlined: `bg-transparent ${theme.text.primary} ${theme.border.default}`,
    underlined: `bg-transparent ${theme.text.primary} border-0 border-b-2 ${theme.border.light} rounded-none`
  };

  // Character count
  const characterCount = inputValue.length;
  const isOverLimit = maxLength && characterCount > maxLength;

  return (
    <div className="relative">
      {/* Main input container */}
      <div className="relative">
        {/* Prefix */}
        {prefix && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none z-10">
            {prefix}
          </div>
        )}
        
        {/* Input field */}
        <input
          ref={inputRef}
          type={type}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          pattern={pattern}
          inputMode={inputMode}
          className={cn(
            'w-full border rounded-md transition-colors',
            sizeClasses[size],
            variantClasses[variant],
            prefix && 'pl-10',
            suffix && 'pr-10',
            showClearButton && (value || inputValue) && !disabled && !readOnly && 'pr-10',
            isFocused && `${theme.border.focus} ring-2 ring-blue-500/20`,
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            disabled && 'opacity-50 cursor-not-allowed',
            readOnly && 'cursor-default bg-gray-50 dark:bg-gray-800',
            isOverLimit && 'border-red-500',
            className
          )}
          {...props}
        />
        
        {/* Suffix */}
        {suffix && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
            {suffix}
          </div>
        )}
        
        {/* Clear button */}
        {showClearButton && (value || inputValue) && !disabled && !readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="Clear text"
          >
            <XIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
          </button>
        )}
      </div>
      
      {/* Helper text and character count */}
      <div className="mt-1 flex justify-between items-start">
        {/* Helper text or error */}
        <div className="flex-1">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          {!error && helperText && (
            <div className={`text-sm ${theme.text.muted}`}>
              {helperText}
            </div>
          )}
        </div>
        
        {/* Character count */}
        {showCharacterCount && maxLength && (
          <div className={`text-xs ml-2 ${
            isOverLimit 
              ? 'text-red-600 dark:text-red-400' 
              : theme.text.muted
          }`}>
            {characterCount}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextInput;
